import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Check, Loader2, Plus, Settings, XCircle } from 'lucide-react';
function parseResult(result) {
    try {
        return JSON.parse(result);
    } catch  {
        return null;
    }
}
/**
 * Beautiful card for displaying request_tools results
 */ export function RequestToolsCard({ toolCall }) {
    const isComplete = !!toolCall.result;
    const hasError = !!toolCall.error;
    const parsed = toolCall.result ? parseResult(toolCall.result) : null;
    if (hasError) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-3 py-2 text-sm text-red-500 bg-red-500/5 border border-red-500/20 rounded-lg",
            children: [
                /*#__PURE__*/ _jsx(XCircle, {
                    className: "h-4 w-4"
                }),
                /*#__PURE__*/ _jsxs("span", {
                    children: [
                        "Tool activation failed: ",
                        toolCall.error
                    ]
                })
            ]
        });
    }
    if (!isComplete) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-muted/30 border rounded-lg",
            children: [
                /*#__PURE__*/ _jsx(Loader2, {
                    className: "h-4 w-4 animate-spin text-amber-500"
                }),
                /*#__PURE__*/ _jsx("span", {
                    children: "Activating tools..."
                })
            ]
        });
    }
    if (!parsed) {
        return /*#__PURE__*/ _jsxs("div", {
            className: "flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground bg-muted/30 border rounded-lg",
            children: [
                /*#__PURE__*/ _jsx(Settings, {
                    className: "h-4 w-4"
                }),
                /*#__PURE__*/ _jsx("span", {
                    children: "Tool activation complete"
                })
            ]
        });
    }
    const hasActivated = Array.isArray(parsed.activated) && parsed.activated.length > 0;
    const hasAlreadyActive = Array.isArray(parsed.already_active) && parsed.already_active.length > 0;
    const hasNotFound = Array.isArray(parsed.not_found) && parsed.not_found.length > 0;
    return /*#__PURE__*/ _jsxs("div", {
        className: "border rounded-lg overflow-hidden bg-muted/20",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center justify-between px-3 py-2 bg-muted/30 border-b",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-2",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "p-1.5 rounded-md bg-amber-500/10",
                                children: /*#__PURE__*/ _jsx(Settings, {
                                    className: "h-4 w-4 text-amber-600"
                                })
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-sm font-medium",
                                children: "Tool Activation"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5",
                        children: [
                            hasActivated && /*#__PURE__*/ _jsxs(Badge, {
                                variant: "default",
                                className: "text-[10px] bg-green-500",
                                children: [
                                    "+",
                                    parsed.activated.length
                                ]
                            }),
                            /*#__PURE__*/ _jsx(Check, {
                                className: "h-4 w-4 text-green-500"
                            })
                        ]
                    })
                ]
            }),
            hasActivated && /*#__PURE__*/ _jsxs("div", {
                className: "px-3 py-2 border-b border-dashed",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 mb-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(Plus, {
                                className: "h-3 w-3 text-green-500"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium text-green-600",
                                children: "Activated"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-1.5",
                        children: parsed.activated.map((tool, idx)=>/*#__PURE__*/ _jsx(Badge, {
                                variant: "secondary",
                                className: "text-xs bg-green-500/10 text-green-700 border-green-500/20",
                                children: tool
                            }, idx))
                    })
                ]
            }),
            hasAlreadyActive && /*#__PURE__*/ _jsxs("div", {
                className: "px-3 py-2 border-b border-dashed",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 mb-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(Check, {
                                className: "h-3 w-3 text-muted-foreground"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium text-muted-foreground",
                                children: "Already Active"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-1.5",
                        children: parsed.already_active.map((tool, idx)=>/*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-xs text-muted-foreground",
                                children: tool
                            }, idx))
                    })
                ]
            }),
            hasNotFound && /*#__PURE__*/ _jsxs("div", {
                className: "px-3 py-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center gap-1.5 mb-1.5",
                        children: [
                            /*#__PURE__*/ _jsx(XCircle, {
                                className: "h-3 w-3 text-red-500"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "text-xs font-medium text-red-500",
                                children: "Not Found"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-1.5",
                        children: parsed.not_found.map((tool, idx)=>/*#__PURE__*/ _jsx(Badge, {
                                variant: "secondary",
                                className: "text-xs bg-red-500/10 text-red-600 border-red-500/20",
                                children: tool
                            }, idx))
                    })
                ]
            })
        ]
    });
}
